import React from 'react';

const LeadDetailModal = ({ lead, onClose }) => {
  if (!lead) return null;

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex justify-between items-center rounded-t-lg">
          <div>
            <h2 className="text-2xl font-bold">{lead.customerName}</h2>
            <p className="text-blue-100 text-sm">Lead Details - Sr. No: {lead.serialNumber || 'N/A'}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-blue-800 rounded-full p-2"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {/* Basic Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-blue-500">
              📋 Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="text-xs text-gray-500 uppercase font-semibold">Client Name</label>
                <p className="text-gray-900 font-medium mt-1">{lead.customerName || 'N/A'}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="text-xs text-gray-500 uppercase font-semibold">Email</label>
                <p className="text-gray-900 font-medium mt-1">{lead.email || 'N/A'}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="text-xs text-gray-500 uppercase font-semibold">Contact Number</label>
                <p className="text-gray-900 font-medium mt-1">{lead.contactNumber || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Service Type - For Other Services */}
          {lead.leadType === 'other_services' && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-teal-500">
                📋 Service Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-teal-50 p-4 rounded-lg border-l-4 border-teal-500">
                  <label className="text-xs text-teal-700 uppercase font-semibold">Service Type</label>
                  <p className="text-gray-900 font-bold mt-1 text-lg">
                    {lead.serviceType ? lead.serviceType.replace(/_/g, ' ').toUpperCase() : 'N/A'}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <label className="text-xs text-gray-500 uppercase font-semibold">Lead Type</label>
                  <p className="text-gray-900 font-medium mt-1">Other Services</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <label className="text-xs text-gray-500 uppercase font-semibold">Status</label>
                  <p className="text-gray-900 font-medium mt-1">
                    <span className="px-2 py-1 bg-teal-100 text-teal-800 rounded text-sm">
                      {lead.status || 'New'}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Tour Details - Only for Tour Booking */}
          {lead.leadType === 'tour_booking' && (
            <>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-purple-500">
                  🎯 Tour Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <label className="text-xs text-gray-500 uppercase font-semibold">Tour Code</label>
                    <p className="text-gray-900 font-medium mt-1">{lead.tourCode || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <label className="text-xs text-gray-500 uppercase font-semibold">Reference</label>
                    <p className="text-gray-900 font-medium mt-1">{lead.reference || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <label className="text-xs text-gray-500 uppercase font-semibold">Inquiry Type</label>
                    <p className="text-gray-900 font-medium mt-1">{lead.inquiryType || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <label className="text-xs text-gray-500 uppercase font-semibold">Destination</label>
                    <p className="text-gray-900 font-medium mt-1">{lead.destination || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Travel Information */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-green-500">
                  ✈️ Travel Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <label className="text-xs text-gray-500 uppercase font-semibold">Travel Date</label>
                    <p className="text-gray-900 font-medium mt-1">{formatDate(lead.travelDate)}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <label className="text-xs text-gray-500 uppercase font-semibold">No. of Nights</label>
                    <p className="text-gray-900 font-medium mt-1">{lead.numberOfNights || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <label className="text-xs text-gray-500 uppercase font-semibold">No. of Pax</label>
                    <p className="text-gray-900 font-medium mt-1">{lead.numberOfPax || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <label className="text-xs text-gray-500 uppercase font-semibold">No. of Children</label>
                    <p className="text-gray-900 font-medium mt-1">{lead.numberOfChildren || '0'}</p>
                  </div>
                </div>
              </div>

              {/* Accommodation */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-orange-500">
                  🏨 Accommodation
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="text-xs text-gray-500 uppercase font-semibold">No. of Rooms</label>
                <p className="text-gray-900 font-medium mt-1">{lead.numberOfRooms || 'N/A'}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="text-xs text-gray-500 uppercase font-semibold">Hotel Category</label>
                <p className="text-gray-900 font-medium mt-1">{lead.hotelCategory || 'N/A'}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="text-xs text-gray-500 uppercase font-semibold">Meal Plan</label>
                <p className="text-gray-900 font-medium mt-1">{lead.mealPlan || 'N/A'}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="text-xs text-gray-500 uppercase font-semibold">Extra Bed</label>
                <p className="text-gray-900 font-medium mt-1">{lead.extraBed || 'None'}</p>
              </div>
            </div>
          </div>

              {/* Transport */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-indigo-500">
                  🚗 Transport
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <label className="text-xs text-gray-500 uppercase font-semibold">Vehicle Type</label>
                    <p className="text-gray-900 font-medium mt-1">{lead.vehicleType || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <label className="text-xs text-gray-500 uppercase font-semibold">Pick-Up</label>
                    <p className="text-gray-900 font-medium mt-1">{lead.pickUp || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Status */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-red-500">
              📝 Status
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="text-xs text-gray-500 uppercase font-semibold">Reservation Status</label>
                <p className="mt-1">
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    {lead.reservationStatus || 'Pending'}
                  </span>
                </p>
              </div>
              {lead.specialNote && (
                <div className="bg-gray-50 p-4 rounded-lg md:col-span-2">
                  <label className="text-xs text-gray-500 uppercase font-semibold">Special Note</label>
                  <p className="text-gray-900 mt-1">{lead.specialNote}</p>
                </div>
              )}
            </div>
          </div>

          {/* Close Button */}
          <div className="flex justify-end pt-4 border-t">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetailModal;
